import { redirect } from 'react-router'
import {
	getMappedMainEntity,
	getRuntimeBrandKey,
	getRuntimeMainEntity,
	type MainEntity,
} from '#app/utils/pz/brand/routeEntityRuntime'

export type { MainEntity } from '#app/utils/pz/brand/routeEntityRuntime'

const DEFAULT_MAIN_ENTITY: MainEntity = 'course'

const MAIN_ENTITY_PATH_OVERRIDES: Record<
	MainEntity,
	{
		adminBase: string
		adminPrefix: string
		dashboardBase: string
		marketingBase: string
	}
> = {
	course: {
		adminBase: '/admin/courses',
		adminPrefix: '/admin/courses-',
		dashboardBase: '/learn/courses',
		marketingBase: '/courses',
	},
	track: {
		adminBase: '/admin/tracks',
		adminPrefix: '/admin/tracks-',
		dashboardBase: '/learn/tracks',
		marketingBase: '/tracks',
	},
}

const MAIN_ENTITY_DASHBOARD_ROUTE_IDS: Record<
	MainEntity,
	{ course: string; topic: string; resource: string }
> = {
	course: {
		course: 'routes/_learn+/learn.$entity.$courseId',
		topic: 'routes/_learn+/learn.$entity.$courseId.topics.$topicId',
		resource: 'routes/_learn+/learn.$entity.$courseId.resources.$resourceId',
	},
	track: {
		course: 'routes/_learn+/learn.$entity.$courseId',
		topic: 'routes/_learn+/learn.$entity.$courseId.topics.$topicId',
		resource: 'routes/_learn+/learn.$entity.$courseId.resources.$resourceId',
	},
}

export type MainEntityDashboardRouteKey =
	keyof (typeof MAIN_ENTITY_DASHBOARD_ROUTE_IDS)['course']

export function getMainEntity(brandKey?: string | null): MainEntity {
	return (
		getRuntimeMainEntity() ??
		getMappedMainEntity(brandKey ?? getRuntimeBrandKey()) ??
		DEFAULT_MAIN_ENTITY
	)
}

function getMainEntityConfig(entity?: MainEntity) {
	const target = entity ?? getMainEntity()
	return (
		MAIN_ENTITY_PATH_OVERRIDES[target] ??
		MAIN_ENTITY_PATH_OVERRIDES[DEFAULT_MAIN_ENTITY]
	)
}

export function getMainEntityDashboardRouteId(
	key: MainEntityDashboardRouteKey = 'course',
	mainEntity?: MainEntity,
): string {
	const entity = mainEntity ?? getMainEntity()
	return (
		MAIN_ENTITY_DASHBOARD_ROUTE_IDS[entity]?.[key] ??
		MAIN_ENTITY_DASHBOARD_ROUTE_IDS[DEFAULT_MAIN_ENTITY][key]
	)
}

export function getMainEntityRedirectResponse(
	request: Request,
): Response | null {
	if (request.method !== 'GET' && request.method !== 'HEAD') {
		return null
	}

	const url = new URL(request.url)
	if (url.pathname.endsWith('.data')) {
		return null
	}
	const currentPath = `${url.pathname}${url.search}`
	const canonicalPath = applyCourseEntityToPath(currentPath)
	const targetPath = applyMainEntityToPath(canonicalPath)
	if (targetPath === currentPath) return null
	return redirect(targetPath)
}

export type MainEntityArea = 'admin' | 'dashboard' | 'marketing'

function getPathSegment(basePath: string, prefix: string) {
	const trimmed = basePath.startsWith(prefix)
		? basePath.slice(prefix.length)
		: basePath
	return trimmed.replace(/^\/+/, '').replace(/\/+$/, '')
}

export function getMainEntitySegment(
	area: MainEntityArea,
	mainEntity?: MainEntity,
): string {
	const config = getMainEntityConfig(mainEntity)
	if (area === 'dashboard') {
		return getPathSegment(config.dashboardBase, '/learn/')
	}
	if (area === 'marketing') {
		return getPathSegment(config.marketingBase, '/')
	}
	return getPathSegment(config.adminBase, '/admin/')
}

export function assertMainEntitySegment(
	area: MainEntityArea,
	segment: string | undefined,
	mainEntity?: MainEntity,
): asserts segment is string {
	const expected = getMainEntitySegment(area, mainEntity)
	if (!segment || segment !== expected) {
		throw new Response('Not Found', { status: 404 })
	}
}

export function applyMainEntityToPath(
	path: string,
	mainEntity?: MainEntity,
): string {
	const baseConfig = getMainEntityConfig(DEFAULT_MAIN_ENTITY)
	const targetConfig = getMainEntityConfig(mainEntity)
	if (targetConfig === baseConfig) return path

	return path
		.replace(
			new RegExp(`^${baseConfig.adminBase}(\\/|$)`),
			`${targetConfig.adminBase}$1`,
		)
		.replace(new RegExp(`^${baseConfig.adminPrefix}`), targetConfig.adminPrefix)
		.replace(
			new RegExp(`^${baseConfig.dashboardBase}(\\/|$)`),
			`${targetConfig.dashboardBase}$1`,
		)
		.replace(
			new RegExp(`^${baseConfig.marketingBase}(\\/|$)`),
			`${targetConfig.marketingBase}$1`,
		)
}

export function applyCourseEntityToPath(path: string): string {
	const courseConfig = getMainEntityConfig(DEFAULT_MAIN_ENTITY)
	const configs = Object.values(MAIN_ENTITY_PATH_OVERRIDES).filter(
		(config) => config !== courseConfig,
	)

	return configs.reduce((current, config) => {
		return current
			.replace(
				new RegExp(`^${config.adminBase}(\\/|$)`),
				`${courseConfig.adminBase}$1`,
			)
			.replace(new RegExp(`^${config.adminPrefix}`), courseConfig.adminPrefix)
			.replace(
				new RegExp(`^${config.dashboardBase}(\\/|$)`),
				`${courseConfig.dashboardBase}$1`,
			)
			.replace(
				new RegExp(`^${config.marketingBase}(\\/|$)`),
				`${courseConfig.marketingBase}$1`,
			)
	}, path)
}
